import React, { ReactNode, useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Vector3 } from 'three';

import { isValidNumber, roundToDecimals } from '../../../../../../../utils/math/number';
import {
  fractionFromSlopePercent,
  slopeDegreesToPercent,
  slopePercentToSignedDegrees,
  vectorToSlopePercent,
} from '../../../../../../../utils/math/slope';
import { isDefined } from '../../../../../../../utils/variables';
import { publish, SubscriptionTopic } from '../../../../../../../viewer/messaging/pubsub';
import {
  DesignTool,
  SnapSettings,
  SnapValues,
} from '../../../../../../../viewer/tools/design-tool';
import { ViewSettings } from '../../../../../../../viewer/view-settings';
import { CheckBox } from '../../../../../check-box/check-box';
import { Icon } from '../../../../../icon/icon';
import { InfoBox } from '../../../../../info-box/info-box';
import { LabelledContainer } from '../../../../../labelled-container/labelled-container';
import { SnapStyleButtons } from '../../../../../snap-style-buttons/snap-style-buttons';
import { Stack } from '../../../../../stack/stack';
import { TextBox } from '../../../../../text-box/text-box';
import { CollapsableSection } from '../../../components/collapsable-section';
import { OptionLabels } from '../../../components/option-labels';
import { PositionInput } from '../../../components/position-input';
import { SnapAssetsDialog } from '../dialogs/snap-assets-dialog';

type Props = {
  expanded: boolean;
  tool: DesignTool;
};

type Choice<T extends string> = {
  value: T;
  title: string;
  tooltip?: ReactNode;
  content?: ReactNode;
};

/**
 * A snap setting that can be turned on and off, with one of its choices selected when it is on.
 */
export function SnapSetting<T extends string>(props: {
  title: string;
  value: T | undefined;
  choices: Choice<T>[];
  onChange: (value: T | undefined) => void;
}) {
  // Remember the last choice so it is selected again when the setting is turned back on.
  const [lastValue, setLastValue] = React.useState<T>(props.value ?? props.choices[0].value);
  const checked = isDefined(props.value);

  return (
    <Stack spacing={0.5}>
      <CheckBox checked={checked} onChange={() => props.onChange(checked ? undefined : lastValue)}>
        {props.title}
      </CheckBox>

      {checked && (
        <SettingContent>
          <OptionLabels
            active={props.value}
            itemRows={[
              props.choices.map((choice) => ({
                title: choice.title,
                value: choice.value,
                tooltipText: choice.tooltip,
                // The key makes sure the content is recreated when switching between choices, so
                // inputs of the same type do not keep the value of the previous choice.
                content: isDefined(choice.content) ? (
                  <React.Fragment key={choice.value}>{choice.content}</React.Fragment>
                ) : undefined,
                onSelect: () => {
                  setLastValue(choice.value);
                  props.onChange(choice.value);
                },
              })),
            ]}
          />
        </SettingContent>
      )}
    </Stack>
  );
}

export const NumberInput = (props: {
  title: string;
  defaultValue: number;
  onChange: (value: number) => void;
  /** Whether a number can be used, any number by default. */
  isValid?: (value: number) => boolean;
}) => {
  const { t } = useTranslation();
  const [value, setValue] = React.useState(props.defaultValue.toString());
  const isValid = (x: number) => isValidNumber(x) && (props.isValid?.(x) ?? true);

  return (
    <LabelledContainer text={props.title}>
      {(formElementId) => (
        <TextBox
          autoFocus={true}
          defaultValue={value}
          errorMessage={
            isValid(parseFloat(value))
              ? undefined
              : t('section.snap.validation.invalidNumber', { ns: 'skyviewDesign' })
          }
          id={formElementId()}
          step={0.01}
          type="number"
          onChange={(e) => {
            setValue(e.target.value);
            const parsedValue = parseFloat(e.target.value);

            if (isValid(parsedValue)) {
              props.onChange(parsedValue);
            }
          }}
        />
      )}
    </LabelledContainer>
  );
};

const SlopeRatioInput = (props: { defaultValue: number; onChange: (value: number) => void }) => {
  const { t } = useTranslation();
  const fraction = fractionFromSlopePercent(props.defaultValue);

  return (
    <PositionInput
      /* X is Height and X is width, so we flip it when we display it. */
      defaultValue={{ x: fraction.y, y: fraction.x, z: 0 }}
      display={[
        t('section.snap.relativeSlopeFractionButton.inputTitle.height', { ns: 'skyviewDesign' }),
        t('section.snap.relativeSlopeFractionButton.inputTitle.width', { ns: 'skyviewDesign' }),
      ]}
      onValueChanged={(point) =>
        props.onChange(vectorToSlopePercent(new Vector3(point.y, 0, point.x)))
      }
    />
  );
};

export const SnappingSection = (props: Props) => {
  const { t } = useTranslation();
  const { tool } = props;
  const [settings, setSettings] = React.useState<SnapSettings>(tool.snapSettings);
  const [values, setValues] = React.useState<SnapValues>(tool.snapValues);
  const [snapTowardAssets, setSnapTowardAssets] = React.useState(tool.snapValues.assets);
  const [snapTowardPointCloud, setSnapTowardPointCloud] = React.useState(
    tool.snapValues.pointCloud,
  );
  const [snapTowardTiledModel, setSnapTowardTiledModel] = React.useState(
    tool.snapValues.tiledModel,
  );
  const [isCadViewer] = React.useState(ViewSettings.instance.isCadViewer);
  const [showSnapAssetsDialog, setShowSnapAssetsDialog] = React.useState(false);
  const [hasCustomSnapAssets, setHasCustomSnapAssets] = React.useState(
    tool.excludedSnapAssetIds.size > 0,
  );

  useEffect(() => {
    tool.onSnapValuesChanged({
      assets: snapTowardAssets,
      pointCloud: snapTowardPointCloud,
      tiledModel: snapTowardTiledModel,
    });
  }, [tool, snapTowardAssets, snapTowardPointCloud, snapTowardTiledModel]);

  useEffect(() => {
    setSettings(tool.snapSettings);
    setValues(tool.snapValues);
    setSnapTowardAssets(tool.snapValues.assets);
    setSnapTowardPointCloud(tool.snapValues.pointCloud);
    setSnapTowardTiledModel(tool.snapValues.tiledModel);
    setHasCustomSnapAssets(tool.excludedSnapAssetIds.size > 0);
  }, [tool, tool.asset]);

  const onSettingsChanged = (newValue: Partial<SnapSettings>) => {
    setSettings((oldValue) => ({ ...oldValue, ...newValue }));
    tool.onSnapSettingsChanged(newValue);
  };

  const onValuesChanged = (newValue: Partial<SnapValues>) => {
    setValues((oldValue) => ({ ...oldValue, ...newValue }));
    tool.onSnapValuesChanged(newValue);
  };

  return (
    <CollapsableSection
      expanded={props.expanded}
      title={t('section.snapping.title', { ns: 'skyviewDesign' })}
      titleUpperCase={true}
      onSetExpanded={(state) =>
        publish(SubscriptionTopic.DesignToolMenuStateChanged, { snapMode: state })
      }
    >
      <Stack spacing={1.5}>
        <SnapStyleButtons
          value={settings.styles}
          onChange={(styles) => onSettingsChanged({ styles })}
        />

        <LabelledContainer
          infoTooltip={t('section.snapping.snapToInfo', { ns: 'skyviewDesign' })}
          text={t('section.snapping.snapTo', { ns: 'skyviewDesign' })}
        >
          <Stack spacing={0.5}>
            {!isCadViewer && (
              <>
                <CheckBox
                  checked={snapTowardPointCloud}
                  onChange={() => setSnapTowardPointCloud((oldValue) => !oldValue)}
                >
                  {t('snap.pointCloud', { ns: 'skyviewDesign' })}
                </CheckBox>
                <CheckBox
                  checked={snapTowardTiledModel}
                  onChange={() => setSnapTowardTiledModel((oldValue) => !oldValue)}
                >
                  {t('snap.tiledModel', { ns: 'skyviewDesign' })}
                </CheckBox>
              </>
            )}
            <Stack alignItems="center" direction="row" spacing={0.5}>
              <CheckBox
                checked={snapTowardAssets}
                onChange={() => setSnapTowardAssets((oldValue) => !oldValue)}
              >
                {t('snap.assets', { ns: 'skyviewDesign' })}
                {hasCustomSnapAssets && (
                  <CustomTag>
                    {t('section.snapping.snapAssets.custom', { ns: 'skyviewDesign' })}
                  </CustomTag>
                )}
              </CheckBox>
              <Icon
                aria-label={t('section.snapping.snapAssets.settings', { ns: 'skyviewDesign' })}
                fixedWidth={true}
                icon={['fal', 'cog']}
                title={t('section.snapping.snapAssets.settings', { ns: 'skyviewDesign' })}
                onClick={() => setShowSnapAssetsDialog(true)}
                onHoverStyle={{ icon: ['fas', 'cog'] }}
              />
            </Stack>
            <SnapSetting
              choices={[
                {
                  value: 'absolute',
                  title: t('section.snapping.height.absolute.text', { ns: 'skyviewDesign' }),
                  tooltip: t('section.snapping.height.absolute.tooltip', {
                    ns: 'skyviewDesign',
                  }),
                  content: (
                    <NumberInput
                      defaultValue={values.height}
                      title={t('section.snapping.height.absolute.inputTitle', {
                        ns: 'skyviewDesign',
                      })}
                      onChange={(height) => onValuesChanged({ height })}
                    />
                  ),
                },
                {
                  value: 'offset',
                  title: t('section.snapping.height.offset.text', {
                    ns: 'skyviewDesign',
                  }),
                  tooltip: t('section.snapping.height.offset.tooltip', {
                    ns: 'skyviewDesign',
                  }),
                  content: (
                    <NumberInput
                      defaultValue={values.heightOffset}
                      title={t('section.snapping.height.offset.inputTitle', {
                        ns: 'skyviewDesign',
                      })}
                      onChange={(heightOffset) => onValuesChanged({ heightOffset })}
                    />
                  ),
                },
              ]}
              title={t('section.snapping.height.text', { ns: 'skyviewDesign' })}
              value={settings.height}
              onChange={(height) => onSettingsChanged({ height })}
            />
          </Stack>
        </LabelledContainer>

        <LabelledContainer
          infoTooltip={t('section.snapping.lines.info', { ns: 'skyviewDesign' })}
          text={t('section.snapping.lines.title', { ns: 'skyviewDesign' })}
        >
          <Stack spacing={0.5}>
            <SnapSetting
              choices={[
                {
                  value: 'perpendicular',
                  title: t('section.snapping.lines.angle.perpendicular.text', {
                    ns: 'skyviewDesign',
                  }),
                  tooltip: (
                    <Trans
                      components={{ bold: <strong />, br: <br /> }}
                      i18nKey="section.snapping.lines.angle.perpendicular.tooltip"
                      ns="skyviewDesign"
                    />
                  ),
                },
                {
                  value: 'custom',
                  title: t('section.snapping.lines.angle.custom.text', { ns: 'skyviewDesign' }),
                  tooltip: t('section.snapping.lines.angle.custom.tooltip', {
                    ns: 'skyviewDesign',
                  }),
                  content: (
                    <NumberInput
                      defaultValue={values.angle}
                      title={t('section.snapping.lines.angle.custom.inputTitle', {
                        ns: 'skyviewDesign',
                      })}
                      onChange={(angle) => onValuesChanged({ angle })}
                    />
                  ),
                },
              ]}
              title={t('section.snapping.lines.angle.text', { ns: 'skyviewDesign' })}
              value={settings.angle}
              onChange={(angle) => onSettingsChanged({ angle })}
            />

            <SnapSetting
              choices={[
                {
                  value: '2d',
                  title: t('section.snapping.lines.length.twoD.text', { ns: 'skyviewDesign' }),
                  tooltip: t('section.snapping.lines.length.twoD.tooltip', {
                    ns: 'skyviewDesign',
                  }),
                  content: (
                    <NumberInput
                      defaultValue={values.length}
                      title={t('section.snapping.lines.length.twoD.inputTitle', {
                        ns: 'skyviewDesign',
                      })}
                      onChange={(length) => onValuesChanged({ length })}
                    />
                  ),
                },
                {
                  value: '3d',
                  title: t('section.snapping.lines.length.threeD.text', { ns: 'skyviewDesign' }),
                  tooltip: t('section.snapping.lines.length.threeD.tooltip', {
                    ns: 'skyviewDesign',
                  }),
                  content: (
                    <NumberInput
                      defaultValue={values.length}
                      title={t('section.snapping.lines.length.threeD.inputTitle', {
                        ns: 'skyviewDesign',
                      })}
                      onChange={(length) => onValuesChanged({ length })}
                    />
                  ),
                },
                {
                  value: 'cutFill',
                  title: t('section.snapping.lines.length.cutFill.text', { ns: 'skyviewDesign' }),
                  tooltip: t('section.snapping.lines.length.cutFill.tooltip', {
                    ns: 'skyviewDesign',
                  }),
                  content: (
                    <InfoBox color="blue" leftIcon={{ icon: 'info' }}>
                      {t('section.snapping.lines.length.cutFill.info', { ns: 'skyviewDesign' })}
                    </InfoBox>
                  ),
                },
              ]}
              title={t('section.snapping.lines.length.text', { ns: 'skyviewDesign' })}
              value={settings.length}
              onChange={(length) => onSettingsChanged({ length })}
            />

            <SnapSetting
              choices={[
                {
                  value: 'slopePercent',
                  title: t('section.snapping.lines.incline.slopePercent.text', {
                    ns: 'skyviewDesign',
                  }),
                  tooltip: t('section.snapping.lines.incline.slopePercent.tooltip', {
                    ns: 'skyviewDesign',
                  }),
                  content: (
                    <NumberInput
                      // Rounded, as a slope typed in degrees seldom is a round percent
                      defaultValue={roundToDecimals(values.slope, 4)}
                      title={t('section.snapping.lines.incline.slopePercent.inputTitle', {
                        ns: 'skyviewDesign',
                      })}
                      onChange={(slope) => onValuesChanged({ slope })}
                    />
                  ),
                },
                {
                  value: 'slopeDegrees',
                  title: t('section.snapping.lines.incline.slopeDegrees.text', {
                    ns: 'skyviewDesign',
                  }),
                  tooltip: t('section.snapping.lines.incline.slopeDegrees.tooltip', {
                    ns: 'skyviewDesign',
                  }),
                  content: (
                    <NumberInput
                      // Stored as a slope in percent, like the other inclines
                      defaultValue={roundToDecimals(slopePercentToSignedDegrees(values.slope), 4)}
                      isValid={(degrees) => Math.abs(degrees) < 90}
                      title={t('section.snapping.lines.incline.slopeDegrees.inputTitle', {
                        ns: 'skyviewDesign',
                      })}
                      onChange={(degrees) =>
                        onValuesChanged({ slope: slopeDegreesToPercent(degrees) })
                      }
                    />
                  ),
                },
                {
                  value: 'slopeRatio',
                  title: t('section.snapping.lines.incline.slopeRatio.text', {
                    ns: 'skyviewDesign',
                  }),
                  tooltip: t('section.snapping.lines.incline.slopeRatio.tooltip', {
                    ns: 'skyviewDesign',
                  }),
                  content: (
                    <SlopeRatioInput
                      defaultValue={values.slope}
                      onChange={(slope) => onValuesChanged({ slope })}
                    />
                  ),
                },
              ]}
              title={t('section.snapping.lines.incline.text', { ns: 'skyviewDesign' })}
              value={settings.incline}
              onChange={(incline) => onSettingsChanged({ incline })}
            />
          </Stack>
        </LabelledContainer>
      </Stack>
      {showSnapAssetsDialog && (
        <SnapAssetsDialog
          tool={tool}
          onClose={() => {
            setShowSnapAssetsDialog(false);
            setHasCustomSnapAssets(tool.excludedSnapAssetIds.size > 0);
          }}
        />
      )}
    </CollapsableSection>
  );
};

const CustomTag = styled.span`
  margin-left: 0.5em;
  color: ${(props) => props.theme.color.gray.dark};
  font-style: italic;
`;

const SettingContent = styled.div`
  padding-left: 1.6em;
`;
